import {
  ContactFormData,
  ContentType,
  ContentTypeName,
  DYNAMIC_CONTENT_TYPE_NAMES,
  DynamicContentType,
  FormData,
  getDefaultSenderConfirmationHtml,
  getDefaultSenderConfirmationSubject,
  GridData,
  jsonCopy,
  NavTreeItem,
  STATIC_CONTENT_TYPE_NAMES,
  WebsiteConfigBM,
  WebsiteConfigPage,
} from '@bm-js/atlascloud-shared'

import { _typeCast } from '@naturalcycles/js-lib/types'
import { v4 as uuid } from 'uuid'
import { formDefaultFields } from './defaults'
import {
  EditorCenterSettings,
  EditorUI,
  FullScreenMode,
} from '../contexts/Editor.context'
import {
  EDITOR_CENTER_PADDING,
  EDITOR_LEFT_BAR_WIDTH,
  EDITOR_RIGHT_BAR_WIDTH,
  EDITOR_TOP_BAR_HEIGHT,
} from '../cnst/editor.cnst'
import { DraggableData } from '../components/Draggable'
import { EditorDroppbleData } from '../components/EditorDroppable'

const injectPrefilledValuesToContentType = ({
  content,
  websiteConfig,
  customerName,
}: NewContentInput) => {
  switch (content.name) {
    case 'form': {
      _typeCast<DynamicContentType<FormData>>(content)
      content.data.contactEmail = websiteConfig.contactInfo?.email || ''
      content.data.fields = formDefaultFields
      content.data.senderConfirmationEmailSubject =
        getDefaultSenderConfirmationSubject(customerName)
      content.data.senderConfirmationEmailBody =
        getDefaultSenderConfirmationHtml(customerName)
      break
    }
    case 'contactform': {
      _typeCast<DynamicContentType<ContactFormData>>(content)
      content.data.responseEmail = websiteConfig.contactInfo?.email || ''
      break
    }
    default:
      break
  }
}

const generateNewContent = ({
  content,
  websiteConfig,
  customerName,
}: NewContentInput): DynamicContentType<unknown> => {
  const newContent = jsonCopy({
    ...content,
    uuid: uuid(),
  })

  injectPrefilledValuesToContentType({ content: newContent, websiteConfig, customerName })
  return newContent
}

export function onDragEnd({
  source,
  destination,
  page,
  websiteConfig,
  customerName,
}: OnDragEndInput): WebsiteConfigPage {
  let destinationGridContent = jsonCopy(destination.gridContent)

  if (source.source === 'sidebar') {
    // Add uuid and prefilled values
    const sourceContent = generateNewContent({ content: source.content, websiteConfig, customerName })

    // Insert into grid
    destinationGridContent.data.content[destination.sectionIndex].splice(
      destination.contentIndex,
      0,
      sourceContent
    )

    // Patch page and return updated page
    return patchGridContentInPage(destinationGridContent, jsonCopy(page))
  }

  // Copy page
  let updatedPage = jsonCopy(page)

  // Remove source content from grid
  const sourceGridContent = jsonCopy(source.source.gridContent)
  sourceGridContent.data.content[source.source.sectionIndex].splice(
    source.source.contentIndex,
    1
  )

  // Patch page with grid that has source removed
  updatedPage = patchGridContentInPage(sourceGridContent, updatedPage)

  // If moving inside the same grid, set source to destination
  if (sourceGridContent.uuid === destinationGridContent.uuid) {
    destinationGridContent = sourceGridContent
  }

  // Insert source content into destination grid
  destinationGridContent.data.content[destination.sectionIndex].splice(
    destination.contentIndex,
    0,
    source.content
  )

  // Patch page and return updated page
  return patchGridContentInPage(destinationGridContent, updatedPage)
}

export function parseNavTree(page: WebsiteConfigPage): NavTreeItem {
  return {
    fullPath: '', // Not used in the editor
    slug: '', // Not used in the editor
    authOnly: page.authOnly,
    uuid: page.uuid,
    viewName: page.viewName,
    children: page.children?.map(parseNavTree) || [],
  }
}

export const findPageByUuid = (
  pages: WebsiteConfigPage[] | undefined,
  uuid: string | null
): WebsiteConfigPage | null => {
  if (!pages) return null
  for (const page of pages) {
    if (page.uuid === uuid) return page
    if (page.children) {
      const found = findPageByUuid(page.children, uuid)
      if (found) return found
    }
  }
  return null
}

export const findContentByUuid = (
  page: WebsiteConfigPage | null,
  uuid: string | null | ContentTypeName
): ContentType<unknown> | null => {
  if (!page || !uuid) return null
  for (const outerContent of page.content) {
    if (outerContent.uuid === uuid) return outerContent
    if (outerContent.name !== 'grid') continue
    const gridContent = outerContent as DynamicContentType<GridData>
    for (const section of gridContent.data.content) {
      for (const innerContent of section) {
        if (innerContent.uuid === uuid) return innerContent
      }
    }
  }
  return null
}

export const patchPageInConfig = (
  updatedPage: Partial<WebsiteConfigPage>,
  config: WebsiteConfigBM
): WebsiteConfigBM => {
  const mapper = (
    current: WebsiteConfigPage,
    updated: Partial<WebsiteConfigPage>
  ): WebsiteConfigPage => {
    const isMatch = current.uuid === updated.uuid
    const merged = isMatch ? { ...current, ...updated } : current
    return {
      ...merged,
      children: merged.children?.map((curr) => mapper(curr, updated)),
    }
  }
  return {
    ...config,
    pages: config.pages.map((curr) => mapper(curr, updatedPage)),
  }
}

export const patchContentInPage = (
  updatedContent: Partial<ContentType<unknown>>,
  page: WebsiteConfigPage
): WebsiteConfigPage => {
  const name = updatedContent.name
  if (!name) return page
  if (updatedContent.name === 'grid') {
    return patchGridContentInPage(
      updatedContent as Partial<ContentType<GridData>>,
      page
    )
  } else if (DYNAMIC_CONTENT_TYPE_NAMES.includes(name)) {
    return patchDynamicContentInPage(updatedContent, page)
  } else if (STATIC_CONTENT_TYPE_NAMES.includes(name)) {
    return patchStaticContentInPage(updatedContent, page)
  }
  return page
}

const patchGridContentInPage = (
  updatedContent: Partial<ContentType<GridData>>,
  page: WebsiteConfigPage
): WebsiteConfigPage => {
  return {
    ...page,
    content: page.content.map((content) => {
      if (content.uuid === updatedContent.uuid) {
        return {
          ...content,
          ...updatedContent,
        }
      }
      return content
    }),
  }
}

const patchDynamicContentInPage = (
  updatedContent: Partial<ContentType<unknown>>,
  page: WebsiteConfigPage
): WebsiteConfigPage => {
  return {
    ...page,
    content: page.content.map((content) => {
      if (content.name !== 'grid') return content
      const gridContent = content as DynamicContentType<GridData>
      return {
        ...gridContent,
        data: {
          ...gridContent.data,
          content: gridContent.data.content.map((section) => {
            return section.map((innerContent) => {
              if (innerContent.uuid === updatedContent.uuid) {
                return { ...innerContent, ...updatedContent }
              }
              return innerContent
            })
          }),
        },
      }
    }),
  }
}

const patchStaticContentInPage = (
  updatedContent: Partial<ContentType<unknown>>,
  page: WebsiteConfigPage
): WebsiteConfigPage => {
  return {
    ...page,
    content: page.content.map((content) => {
      if (content.uuid === updatedContent.uuid) {
        return { ...content, ...updatedContent }
      }
      return content
    }),
  }
}

export const removePageFromConfig = (
  config: WebsiteConfigBM,
  pageUuidToRemove: string
) => {
  const removePage = (pages: WebsiteConfigPage[]) => {
    return pages
      .map((page) => {
        if (page.uuid === pageUuidToRemove) return null
        if (page.children) {
          page.children = removePage(page.children)
        }
        return page
      })
      .filter((p) => p !== null)
  }

  return {
    ...config,
    pages: removePage(config.pages),
  }
}

export const addPageToPages = (
  currentPages: WebsiteConfigPage[],
  newPage: WebsiteConfigPage,
  asChildOfUuid: string | undefined
): WebsiteConfigPage[] => {
  if (!asChildOfUuid) return [...currentPages, newPage]
  return currentPages.map((page) => {
    if (page.uuid === asChildOfUuid) {
      return {
        ...page,
        children: [...(page.children || []), newPage],
      }
    } else {
      return {
        ...page,
        children: addPageToPages(page.children || [], newPage, asChildOfUuid),
      }
    }
  })
}

export const getBreadcrumbs = (
  pages: WebsiteConfigPage[] = [],
  activePage: WebsiteConfigPage | null
): WebsiteConfigPage[] => {
  if (!activePage) return []
  const { uuid } = activePage

  for (const page of pages) {
    if (page.uuid === uuid) return [page]
    if (!page.children) return []

    for (const child of page.children) {
      if (child.uuid === uuid) return [page, child]
      if (!child.children) return []

      for (const grandChild of child.children) {
        if (grandChild.uuid === uuid) return [page, child, grandChild]
        if (!grandChild.children) return []

        for (const grandGrandChild of grandChild.children) {
          if (grandGrandChild.uuid === uuid)
            return [page, child, grandChild, grandGrandChild]
        }
      }
    }
  }

  return []
}

export const getEditorCenterSettings = ({
  fullScreen,
  height,
  leftBarOpen,
  rightBarOpen,
  width,
}: EditorUI & {
  rightBarOpen: boolean
  width: number
  height: number
}): EditorCenterSettings => {
  if (fullScreen === FullScreenMode.desktop) {
    return {
      width: width - EDITOR_CENTER_PADDING * 2,
      height: height - EDITOR_CENTER_PADDING * 2,
      left: EDITOR_CENTER_PADDING,
      top: EDITOR_CENTER_PADDING,
    }
  }

  if (fullScreen === FullScreenMode.mobile) {
    const mobileWidth = 400
    const left = width / 2 - mobileWidth / 2
    return {
      width: mobileWidth,
      height: height - EDITOR_CENTER_PADDING * 2,
      left,
      top: EDITOR_CENTER_PADDING,
    }
  }

  width = width - EDITOR_CENTER_PADDING * 2
  height = height - EDITOR_CENTER_PADDING * 2 - EDITOR_TOP_BAR_HEIGHT
  let left = EDITOR_CENTER_PADDING
  const top = EDITOR_CENTER_PADDING

  if (leftBarOpen) {
    left += EDITOR_LEFT_BAR_WIDTH
    width -= EDITOR_LEFT_BAR_WIDTH
  }

  if (rightBarOpen) width -= EDITOR_RIGHT_BAR_WIDTH

  return { width, height, left, top }
}

interface NewContentInput {
  content: DynamicContentType<unknown>
  websiteConfig: WebsiteConfigBM
  customerName: string
}

interface OnDragEndInput {
  source: DraggableData
  destination: EditorDroppbleData
  page: WebsiteConfigPage
  websiteConfig: WebsiteConfigBM
  customerName: string
}
